
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export type CategoryOrTrade = {
  id: string;
  name: string;
  description?: string;
  is_default?: boolean;
};

export const useCategoriesAndTrades = () => {
  const [categories, setCategories] = useState<CategoryOrTrade[]>([]);
  const [trades, setTrades] = useState<CategoryOrTrade[]>([]);
  const [profile, setProfile] = useState<{ company_id?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get user profile for company_id
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();
          
        if (error && error.code !== 'PGRST116') { // Ignore no rows returned error
          console.error('Error fetching user profile:', error);
          setProfile(null);
        } else {
          setProfile(data || { company_id: undefined });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserProfile();
  }, []);

  // Fetch categories and trades
  useEffect(() => {
    if (!profile?.company_id) {
      setCategories([]);
      setTrades([]);
      setIsLoading(false);
      return;
    }
    
    const fetchCategoriesAndTrades = async () => {
      try {
        setIsLoading(true);
        
        // Fetch categories using the database function
        const { data: categoriesData, error: categoriesError } = await supabase
          .rpc('get_all_categories', { user_company_id: profile.company_id });
          
        if (categoriesError) {
          throw categoriesError;
        }
        
        // Fetch trades using the database function
        const { data: tradesData, error: tradesError } = await supabase
          .rpc('get_all_trades', { user_company_id: profile.company_id });
          
        if (tradesError) {
          throw tradesError;
        }
        
        console.log("Raw categories data:", categoriesData);
        console.log("Raw trades data:", tradesData);
        
        // Ensure we're setting empty arrays if no data is returned
        // And filter out any items that don't have required properties
        const validCategories = Array.isArray(categoriesData) 
          ? categoriesData
              .filter(item => item && typeof item === 'object' && item.id && item.name)
              .map(item => ({
                id: item.id,
                name: item.name,
                description: item.description,
                is_default: item.is_default
              }))
          : [];

        const validTrades = Array.isArray(tradesData) 
          ? tradesData
              .filter(item => item && typeof item === 'object' && item.id && item.name)
              .map(item => ({
                id: item.id,
                name: item.name,
                description: item.description,
                is_default: item.is_default
              }))
          : [];
          
        console.log("Processed categories:", validCategories);
        console.log("Processed trades:", validTrades);
        
        setCategories(validCategories);
        setTrades(validTrades);
      } catch (error) {
        console.error('Error fetching categories and trades:', error);
        toast.error('Failed to load categories and trades');
        // Initialize as empty arrays when there's an error
        setCategories([]);
        setTrades([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCategoriesAndTrades();
  }, [profile]);

  return {
    categories: categories || [],
    trades: trades || [],
    isLoading,
    hasCompany: Boolean(profile?.company_id)
  };
};
